/* eslint-disable max-lines */
import { Selectable } from '@hakimo-ui/shared/ui-base';
import { Linkable } from '../common';
import { Location } from '../location';
import { AlarmTalkdownState, ScanAlarmUpdateType } from '../location-alarms';
import type { Media } from '../unified-alarm';

export interface AlarmsDTO {
  total: number;
  items: Alarm[];
}

export interface AlarmUpdatesDTO {
  items: AlarmUpdate[];
}

export interface NeighboringAlarmsDTO {
  items: ReducedAlarm[];
  curIdx: number;
}

export interface ReducedAlarm {
  id: string;
  type: string;
  timestamp: string;
}

export interface RgMatchedFace {
  profileId: string;
  profileName: string;
  profileType: string;
  employeeId?: string;
  badgeId?: string;
  imagePath: string;
  fullBodyImagePath?: string | null;
  personBox?: number[]; // [x, y, width, height]
  faceBox: number[]; // [x, y, width, height]
  similarityScore: number;
  gender?: string;
  frameUrl?: string; // frame snapshot to crop the detected face from
  // Seconds into the alarm video for the detection. Video-relative to the
  // same origin as Alarm.videoStartTime / AlarmVideoDetails.startTime —
  // both are ai-engine's alarm.video_start_timestamp_utc verbatim, just
  // surfaced under different JSON keys on different endpoints.
  // null when the detection has no frame (e.g. a badge mismatch).
  videoTimeSec?: number | null;
}

/** A camera or door a raw alarm was raised on. */
export interface RawAlarmEntity {
  id: string;
  name?: string;
  audio_devices?: Array<{ id: string }>;
  livestreamUrl?: string;
  /** 'CAMERA' on remote guarding, 'DOOR' on enterprise. */
  type?: 'CAMERA' | 'DOOR';
}

export interface MotionAlarm {
  id: string;
  timestamp: string;
  videoStartTime?: string;
  videoEndTime?: string;
  // What the alarm was raised on, which is not always a camera: enterprise
  // alarms are raised on a door, and its id is the alarm's source group.
  sourceEntity: RawAlarmEntity;
  // The camera that saw it. Remote guarding repeats `sourceEntity` here; on
  // enterprise this is the only place the real camera appears.
  camera?: RawAlarmEntity;
  status?: string;
  tap?: number;
  media?: Media;
  matchedFaces?: RgMatchedFace[];
}

export interface Alarm extends ReducedAlarm {
  status: AlarmStatus;
  tap: number | null;
  sourceSystem: string;
  location?: Location;
  sourceEntity?: SourceEntity;
  tags?: string[];
  employee?: Employee;
  localTime?: string;
  timeToRemediationInSeconds?: number;
  resolvedAt?: string;
  camera?: string;
  videoPath?: string;
  sop?: string;
  imagePath?: string;
  floorplanPath?: string;
  videoDetails?: AlarmVideoDetails;
  media?: Media;
  tenant: AlarmTenant;
  metadata?: AlarmMetadata;
  videoStartTime?: string;
  videoEndTime?: string;
  matchedFaces?: RgMatchedFace[];
}

export type ScanAlarmStatus = 'PROCESSED' | 'UNPROCESSED';
export interface AlarmMetadata {
  badgeNumber: string;
}
export interface AlarmTenant extends Linkable {
  config?: {
    alarmTypeColorCoding?: boolean;
    [key: string]: any;
  };
}
export interface SourceEntity extends Linkable {
  type: 'CAMERA' | 'DOOR';
  cameraId?: string;
}
export interface Employee extends Linkable {
  phoneNumber: string;
}
export interface AlarmMedia {
  videoPath?: string;
  imagePath?: string;
  videoDetails?: AlarmVideoDetails;
  boxes?: Box[];
  media?: Media;
}

export enum BoxType {
  PERSON = 'PERSON',
  VEHICLE = 'MOVING VEHICLE',
  PERSON_WITH_WEAPON = 'PERSON_WITH_WEAPON',
  FACE = 'FACE',
  LOITERING_PERSON = 'LOITERING_PERSON',
}
export interface Box {
  frame: number; // -1 if applicable to entire video
  coords: number[]; // Top left x, y, width, height
  type?: BoxType;
}
export interface AlarmVideoDetails {
  markers: AlarmVideoMarker[];
  startTime: string | null;
}
export interface AlarmVideoMarker {
  timestamp: number; // time in seconds since start of video
  label: string; // string to be displayed
  type: 'Error' | 'Info' | 'Warning';
}
export const statuses = [
  'Resolved by Hakimo',
  'Analyzing',
  'Pending',
  'Resolved Manually',
  'Alarm Acknowledged via ACS',
  'In Progress',
] as const;

export type AlarmStatus = (typeof statuses)[number];
export enum StatusType {
  RESOLVED = 'Resolved',
  IN_PROGRESS = 'In Progress',
  PENDING = 'Pending',
}
export const displayStatusTypes = [
  StatusType.RESOLVED,
  StatusType.IN_PROGRESS,
  StatusType.PENDING,
];
export interface AlarmUpdate {
  feedback?: Feedback;
  newStatus?: StatusType;
  comment?: string;
  timestamp: string;
  // here onwards is only when fetching from backend
  event?: string; // type of alarm update to help with easy parsing
  user?: Selectable; // user who made update
  status?: AlarmStatus; // existing status
  audio_url?: string;
  talkdown?: AlarmTalkdownState[];
  event_id?: string;
  recommendation?: string;
  update_time?: string;
  summary?: string;
  update_type?: ScanAlarmUpdateType;
  update_text?: string;
  twilio_ssid?: string;
  twilio_calling_to_name?: string;
}

export interface Feedback {
  hakimoOutput: HakimoOutput;
  extraFeedback?: ExtraFeedback[];
}

export type HakimoOutput = 'correct' | 'incorrect';

export type ExtraFeedback =
  | 'missingTag'
  | 'extraTag'
  | 'tapTooHigh'
  | 'tapTooLow';

export interface SharedAlarmTokenRequestDTO {
  alarmId: string;
  duration: number;
  durationUnit: string;
}

export interface SharedResourceTokenResponseDTO {
  accessToken: string;
}

export interface BugReport {
  description: string;
  severity: BugReportSeverity;
  bugReporter?: string | null;
}
export type BugReportSeverity = 'low' | 'medium' | 'high';

export interface SMSDTO {
  to_number: string;
  message: string;
  location_id?: number;
  location_alarm_id?: number;
  alarm_group_id?: string;
  to_name?: string;
  snapshots?: File[];
}

export type LocationAlarmPayload = { alarmId: string };

export type AlarmGroupPayload = { alarmGroupId: string; product: string };

export type ViewAlarmPayload = LocationAlarmPayload | AlarmGroupPayload;

export type GetAlarmsMediaPayload = {
  alarmIds: string[];
  includeBoxes: boolean;
  timestamp: string;
  locationAlarmId?: string;
};

export type AlarmMediaResponse = {
  alarmId: string;
  boxes: Box[];
  url: string;
  startTime: string;
  endTime: string;
};
